import React, { useRef, useState, useLayoutEffect } from 'react';
import {
    motion,
    useScroll,
    useTransform,
    useMotionValueEvent,
} from 'framer-motion';

import s from './ServiceCard.module.scss';

const ServiceCard = ({ title = '', layers = [] }) => {
    const scrollRef = useRef(null);

    const { scrollYProgress: progressToTop } = useScroll({
        target: scrollRef,
        offset: ['start 80%', 'start 0%'],
    });

    const { scrollY } = useScroll();
    const [exitPoints, setExitPoints] = useState({ start: 0, end: 0 });

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const { top: rectTop, height } = el.getBoundingClientRect();
        const topAbs = rectTop + window.scrollY;
        const vh = window.innerHeight;
        setExitPoints({ start: topAbs + height - vh, end: topAbs });
    }, []);

    const exitProgress = useTransform(
        scrollY,
        [exitPoints.end, exitPoints.start],
        [0, 1],
        { clamp: true }
    );

    const [showTitle, setShowTitle] = useState(false);
    const [titleKey, setTitleKey] = useState(0);

    useMotionValueEvent(progressToTop, 'change', (v) => {
        if (v >= 1 && !showTitle) {
            setShowTitle(true);
            setTitleKey((k) => k + 1);
        }
        if (v < 0.95 && showTitle) {
            setShowTitle(false);
        }
    });

    const scale = useTransform(progressToTop, [0, 1], [0.5, 1]);
    const x = useTransform(progressToTop, [0, 1], ['70vw', '0vw']);
    const y = useTransform(progressToTop, [0, 1], ['10vh', '0vh']);
    const opacity = useTransform(progressToTop, [0, 1], [0.5, 1]);

    const [activeIdx, setActiveIdx] = useState(0);
    useMotionValueEvent(exitProgress, 'change', (v) => {
        const idx = Math.floor(v * 3);
        setActiveIdx(Math.min(Math.max(idx, 0), 2));
    });

    const [label1, label2, label3] = layers;

    return (
        <div className={s.Root} ref={scrollRef}>
            <motion.div
                className={s.Container}
                style={{ scale, x, y, opacity }}>
                <div className={s.Content}>
                    <div className={s.LayeredFeature}>
                        {/* Layer 1 */}
                        <motion.div
                            className={[
                                s.LayerContainer,
                                s.IsLayer1,
                                activeIdx === 0 ? s.Active : '',
                            ].join(' ')}
                            initial={{ y: 20 }}
                            animate={showTitle ? { y: 0 } : { y: 20 }}
                            transition={{ ease: 'easeInOut', duration: 0.6 }}>
                            <div className={s.LayerSplit}>
                                <div
                                    className={[
                                        s.TriangleUp,
                                        s.Layer1,
                                        activeIdx === 0 ? s.Active : '',
                                    ].join(' ')}
                                />
                                <motion.div
                                    className={[
                                        s.HexMiddle,
                                        s.Layer1,
                                        activeIdx === 0 ? s.Active : '',
                                    ].join(' ')}
                                    animate={{
                                        scaleY: showTitle ? 0 : 1,
                                    }}
                                    initial={{ scaleY: 1 }}
                                    transition={{
                                        ease: 'easeInOut',
                                        duration: 0.6,
                                    }}
                                    style={{
                                        transformOrigin: 'top',
                                        overflow: 'hidden',
                                    }}
                                />

                                <motion.div
                                    className={[
                                        s.TriangleDown,
                                        s.Layer1,
                                        activeIdx === 0 ? s.Active : '',
                                    ].join(' ')}
                                    animate={{
                                        y: showTitle ? '-200%' : '0%',
                                    }}
                                    initial={{ y: '0%' }}
                                    transition={{
                                        ease: 'easeInOut',
                                        duration: 0.6,
                                    }}
                                />
                            </div>
                            {showTitle && (
                                <motion.div
                                    className={`${s.LabelContainer} ${s.Left} ${s.HideOnMobile}`}
                                    style={{
                                        opacity: activeIdx === 0 ? 1 : 0,
                                    }}>
                                    <div className={s.Line} />
                                    <div className={s.Label}>{label1}</div>
                                </motion.div>
                            )}
                        </motion.div>

                        {/* Layer 2 */}
                        <motion.div
                            className={[
                                s.LayerContainer,
                                s.IsLayer2,
                                activeIdx === 1 ? s.Active : '',
                            ].join(' ')}
                            initial={{ y: 20 }}
                            animate={showTitle ? { y: 0 } : { y: 20 }}
                            transition={{ ease: 'easeInOut', duration: 0.6 }}>
                            <div
                                className={[
                                    s.Layer,
                                    s.Layer2,
                                    activeIdx === 1 ? s.Active : '',
                                ].join(' ')}
                            />
                            {showTitle && (
                                <motion.div
                                    className={`${s.LabelContainer} ${s.Right} ${s.HideOnMobile}`}
                                    style={{
                                        opacity: activeIdx === 1 ? 1 : 0,
                                    }}>
                                    <div className={s.Label}>{label2}</div>
                                    <div className={s.Line} />
                                </motion.div>
                            )}
                        </motion.div>

                        {/* Layer 3 */}
                        <motion.div
                            className={[
                                s.LayerContainer,
                                s.IsLayer3,
                                activeIdx === 2 ? s.Active : '',
                            ].join(' ')}
                            initial={{ y: 20 }}
                            animate={showTitle ? { y: 0 } : { y: 20 }}
                            transition={{ ease: 'easeInOut', duration: 0.6 }}>
                            <div
                                className={[
                                    s.Layer,
                                    s.Layer3,
                                    activeIdx === 2 ? s.Active : '',
                                ].join(' ')}
                            />
                            {showTitle && (
                                <motion.div
                                    className={`${s.LabelContainer} ${s.Left} ${s.HideOnMobile}`}
                                    style={{
                                        opacity: activeIdx === 2 ? 1 : 0,
                                    }}>
                                    <div className={s.Line} />
                                    <div className={s.Label}>{label3}</div>
                                </motion.div>
                            )}
                        </motion.div>
                    </div>

                    {showTitle && (
                        <motion.h2
                            key={titleKey}
                            className={s.Title}
                            initial={{ y: 200 }}
                            animate={{ y: 0 }}
                            transition={{ ease: 'easeInOut', duration: 0.6 }}>
                            {title}
                        </motion.h2>
                    )}

                    <motion.div
                        className={s.LabelContainerMobile}
                        initial={{ opacity: 0 }}
                        animate={showTitle ? { opacity: 1 } : { opacity: 0 }}
                        transition={{
                            ease: 'easeInOut',
                            duration: 0.6,
                            delay: 0.3,
                        }}>
                        <div className={s.Label}>
                            {[label1, label2, label3][activeIdx]}
                        </div>
                    </motion.div>
                </div>
            </motion.div>
        </div>
    );
};

export default ServiceCard;
